import type { TFunction } from 'i18next';
import { PROFILE_KEYS } from '../../../i18n/constants/keys';
import { CareerStatus, DEFAULT_CAREER_STATUS, PROFILE_STORAGE_KEYS } from '../constants';
import type { INotificationSetting, IProfileFormState } from '../interfaces';

const readStored = (key: string): string => localStorage.getItem(key) || '';

export const readProfileFormFromStorage = (): IProfileFormState => ({
  firstName: readStored(PROFILE_STORAGE_KEYS.FIRST_NAME),
  lastName: readStored(PROFILE_STORAGE_KEYS.LAST_NAME),
  email: readStored(PROFILE_STORAGE_KEYS.EMAIL),
  profileLink: readStored(PROFILE_STORAGE_KEYS.PROFILE_PIC),
  phone: readStored(PROFILE_STORAGE_KEYS.PHONE),
  jobTitle: readStored(PROFILE_STORAGE_KEYS.JOB_TITLE),
  jobTitleId: readStored(PROFILE_STORAGE_KEYS.JOB_TITLE_ID),
  bio: readStored(PROFILE_STORAGE_KEYS.BIO),
  profilePicStatus: readStored(PROFILE_STORAGE_KEYS.PROFILE_PIC_STATUS),
});

export const readCareerStatusFromStorage = (): CareerStatus => {
  const stored = localStorage.getItem(PROFILE_STORAGE_KEYS.JOB_STATUS);
  return Object.values(CareerStatus).includes(stored as CareerStatus)
    ? (stored as CareerStatus)
    : DEFAULT_CAREER_STATUS;
};

export const readUserRole = (): string =>
  localStorage.getItem(PROFILE_STORAGE_KEYS.ROLE) || 'User';

export const readIsEmployee = (): boolean =>
  (localStorage.getItem(PROFILE_STORAGE_KEYS.CLASS) || '').endsWith('MongoEmployee');

export const getUserInitials = (firstName: string, lastName: string): string =>
  `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

export const getCareerStatusLabel = (t: TFunction, status: CareerStatus): string => {
  const map: Record<CareerStatus, string> = {
    [CareerStatus.ACTIVELY_LOOKING]: t(
      PROFILE_KEYS.sections.candidate.careerStatus.activelyLooking,
    ),
    [CareerStatus.OPEN_TO_OPPORTUNITIES]: t(
      PROFILE_KEYS.sections.candidate.careerStatus.openToOpportunities,
    ),
    [CareerStatus.NOT_AVAILABLE]: t(
      PROFILE_KEYS.sections.candidate.careerStatus.notAvailable,
    ),
  };
  return map[status];
};

export const getDefaultNotifications = (): INotificationSetting[] => [
  {
    id: 'email_updates',
    labelKey: PROFILE_KEYS.sections.notifications.items.emailUpdates.label,
    descriptionKey: PROFILE_KEYS.sections.notifications.items.emailUpdates.description,
    enabled: true,
  },
  {
    id: 'assessment_reminders',
    labelKey: PROFILE_KEYS.sections.notifications.items.assessmentReminders.label,
    descriptionKey: PROFILE_KEYS.sections.notifications.items.assessmentReminders.description,
    enabled: true,
  },
  {
    id: 'system_notifications',
    labelKey: PROFILE_KEYS.sections.notifications.items.systemNotifications.label,
    descriptionKey: PROFILE_KEYS.sections.notifications.items.systemNotifications.description,
    enabled: false,
  },
];

export const hasStoredProfilePicture = (profileLink: string): boolean =>
  Boolean(profileLink) && profileLink !== 'null';
